/**
 * Lightweight Swiper loader
 * Lazily injects the large Swiper bundle only when a slider is present.
 * 
 * Performance optimizations:
 * - Relies on manual triggers and Shopify section events (zero overhead)
 * - Cached customElements checks
 * - Proper cleanup of IntersectionObserver instances
 * - Uses customElements.whenDefined for reliable element registration
 * - Optimized querySelectorAll with combined selector
 * 
 * Configuration:
 * - data-swiper-src: URL to swiper bundle
 * - data-swiper-selector: CSS selector(s) for swiper containers (default: 'swiper-container')
 * - data-swiper-root-margin: IntersectionObserver rootMargin (default: '200px')
 */
(function swiperDeferredLoader() {
    const currentScript = document.currentScript;
    if (!currentScript || !currentScript.dataset) return;
  
    const swiperSrc = currentScript.dataset.swiperSrc;
    const selectors = currentScript.dataset.swiperSelector || 'swiper-container';
    const rootMargin = currentScript.dataset.swiperRootMargin || '200px';
  
    if (!swiperSrc || !selectors) return;
  
    const selectorList = selectors
      .split(',')
      .map((sel) => sel.trim())
      .filter(Boolean);
  
    // Combine selectors for single querySelectorAll call (performance optimization)
    const combinedSelector = selectorList.join(',');
  
    let loadPromise = null;
    let intersectionObservers = new Set();
    let isWatchingDynamicContent = false;
    let isSwiperLoaded = false;
    let evaluateTimer = null;
    let loadErrorCount = 0;
    const MAX_LOAD_RETRIES = 2;
    const LOAD_TIMEOUT = 15000; // 15 seconds
  
    // Add 'swiper-js-loaded' class to body when Swiper is loaded
    const markSwiperLoaded = () => {
      const body = document.body;
      if (body && !body.classList.contains('swiper-js-loaded')) {
        body.classList.add('swiper-js-loaded');
      }
    };
  
    // Cache customElements check result
    const isSwiperRegistered = () => {
      if (isSwiperLoaded) {
        markSwiperLoaded();
        return true;
      }
      isSwiperLoaded = !!window.customElements?.get('swiper-container');
      if (isSwiperLoaded) markSwiperLoaded();
      return isSwiperLoaded;
    };
  
    const querySliders = () => [...document.querySelectorAll(combinedSelector)];
  
    const cleanupIntersectionObservers = () => {
      intersectionObservers.forEach(o => o.disconnect());
      intersectionObservers.clear();
    };
  
    const stopDynamicWatchers = () => {
      cleanupIntersectionObservers();
      if (evaluateTimer) {
        clearTimeout(evaluateTimer);
        evaluateTimer = null;
      }
      if (isWatchingDynamicContent) {
        document.removeEventListener('shopify:section:load', deferredEvaluate);
        document.removeEventListener('shopify:section:reorder', deferredEvaluate);
        isWatchingDynamicContent = false;
      }
    };
  
    const loadSwiperBundle = () => {
      if (isSwiperRegistered()) {
        stopDynamicWatchers();
        return Promise.resolve();
      }
  
      if (loadPromise) {
        return loadPromise;
      }
  
      if (loadErrorCount >= MAX_LOAD_RETRIES) {
        return Promise.reject(new Error('Swiper load failed after maximum retries'));
      }
  
      loadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = swiperSrc;
        script.async = true;
        script.dataset.swiperBundle = 'true';
        
        let timeoutId = null;
        
        // Add network timeout
        timeoutId = setTimeout(() => {
          loadPromise = null;
          loadErrorCount++;
          script.remove();
          reject(new Error(`Swiper load timeout after ${LOAD_TIMEOUT}ms`));
        }, LOAD_TIMEOUT);
        
        // Use customElements.whenDefined for reliable detection
        const checkRegistration = () => {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          
          const onSuccess = () => {
            loadErrorCount = 0;
            stopDynamicWatchers();
            globalThis.scheduleSwiperNavA11ySync?.();
            resolve();
          };
          
          if (isSwiperRegistered()) {
            onSuccess();
          } else {
            setTimeout(() => isSwiperRegistered() ? onSuccess() : resolve(), 200);
          }
        };
        
        script.addEventListener(
          'load',
          () => {
            // Try customElements.whenDefined first (more reliable)
            const ce = window.customElements;
            if (ce?.whenDefined) {
              ce.whenDefined('swiper-container')
                .then(() => {
                  isSwiperLoaded = true;
                  checkRegistration();
                })
                .catch(checkRegistration);
            } else {
              checkRegistration();
            }
          },
          { once: true }
        );
        
        script.addEventListener('error', () => {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          loadPromise = null;
          loadErrorCount++;
          script.remove();
          stopDynamicWatchers();
          reject(new Error(`Failed to load Swiper script${loadErrorCount >= MAX_LOAD_RETRIES ? ' (max retries)' : ''}`));
        }, { once: true });
        
        document.head.appendChild(script);
      });
  
      return loadPromise;
    };
  
    const intersectionHandler = (entries, observer) => {
      if (entries.some(e => e.isIntersecting)) {
        observer.disconnect();
        intersectionObservers.delete(observer);
        loadSwiperBundle();
      }
    };
  
    const watchVisibility = (sliders) => {
      if (!('IntersectionObserver' in window)) {
        loadSwiperBundle();
        return;
      }
  
      const observer = new IntersectionObserver(intersectionHandler, {
        rootMargin,
        threshold: 0.01
      });
      
      intersectionObservers.add(observer);
      sliders.forEach((slider) => observer.observe(slider));
    };
  
    const evaluateSliders = () => {
      if (isSwiperRegistered()) {
        stopDynamicWatchers();
        return true;
      }
      
      const sliders = querySliders();
      if (!sliders.length) return false;
  
      const visible = sliders.some(s => {
        const rect = s.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0;
      });
      return visible ? (loadSwiperBundle(), true) : (watchVisibility(sliders), true);
    };
  
    const deferredEvaluate = () => {
      if (evaluateTimer) clearTimeout(evaluateTimer);
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(evaluateSliders, { timeout: 500 });
      } else {
        evaluateTimer = setTimeout(evaluateSliders, 0);
      }
    };
  
    const watchForDynamicSliders = () => {
      if (isSwiperRegistered() || isWatchingDynamicContent) return;
      document.addEventListener('shopify:section:load', deferredEvaluate, { passive: true });
      document.addEventListener('shopify:section:reorder', deferredEvaluate, { passive: true });
      isWatchingDynamicContent = true;
    };
  
    const init = () => {
      if (isSwiperRegistered()) return;
      if (!evaluateSliders() || !isSwiperRegistered()) {
        watchForDynamicSliders();
      }
    };
  
    window.triggerSwiperLoad = () => {
      if (isSwiperRegistered() || loadPromise) {
        return loadPromise || Promise.resolve();
      }
      const sliders = querySliders();
      if (sliders.length) {
        const visible = sliders.some(s => {
          const rect = s.getBoundingClientRect();
          return rect.top < window.innerHeight && rect.bottom > 0;
        });
        if (visible) {
          return loadSwiperBundle();
        }
        // If not visible, watch for visibility but don't load immediately
        watchVisibility(sliders);
        return Promise.resolve();
      }
      watchForDynamicSliders();
      return Promise.resolve();
    };
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  })();